import { useState, useEffect, useCallback, useRef, ChangeEvent, useMemo, KeyboardEvent, Fragment } from "react";
import cn from "classnames";
import { useQueryClient } from "@tanstack/react-query";
import { Popover, PopoverButton, PopoverPanel, Transition, CloseButton } from "@headlessui/react";
import { usePhoneCall, useCallStatus, CALL_STATUS_QUERY_KEY, PhoneNumber } from "@/state/hooks/usePhoneCall";
import { useContactPhones } from "@/state/hooks/useContactPhones";
import type { ContactPhone } from "@/common/utils/types";
import { useAppSelector } from "@/common/hooks/useRedux";
import { formatPhoneForDisplay, formatDialedNumber } from "@/common/utils/phone";
import { formatDestination } from "@/features/phone-call-widget/formatDestination";
import PhoneIcon from "@/assets/hd-icons/phone-01.svg?react";
import ChevronDownIcon from "@/assets/hd-icons/chevron-down.svg?react";
import BackspaceIcon from "@/assets/hd-icons/backspace.svg?react";
import { getPatientContact, getPatientRecordId } from "../patient-view/state/redux/activePatientDataSlice";
import { toast } from "react-toastify";
import { useAssetLibraryProvider } from "@/state/providers/AssetLibraryProvider";
import ConfirmModal from "@/common/components/ConfirmModal";
import { voipAnalytics } from "@/common/utils/voipAnalytics";

type DialerState = "idle" | "calling" | "in-call";

interface DialerProps {
    onCancel: () => void;
    onAddCallerId: () => void;
    onAddDestination: () => void;
    // Reports whether the Client numbers picker row is on screen, so the widget can grow to fit it.
    onClientNumbersVisibleChange?: (visible: boolean) => void;
}

// Dial pad button configuration
const DIAL_PAD_BUTTONS = [
    { digit: "1", letters: "" },
    { digit: "2", letters: "ABC" },
    { digit: "3", letters: "DEF" },
    { digit: "4", letters: "GHI" },
    { digit: "5", letters: "JKL" },
    { digit: "6", letters: "MNO" },
    { digit: "7", letters: "PQRS" },
    { digit: "8", letters: "TUV" },
    { digit: "9", letters: "WXYZ" },
    { digit: "*", letters: "" },
    { digit: "0", letters: "+" },
    { digit: "#", letters: "" },
] as const;

const MAX_DIALED_DIGITS = 13; // 10 digits + 3 for country code (e.g. +380 (XXX) XXX-XXXX)
const MIN_DIALED_DIGITS_FOR_CALL = 10; // 10 digits (country code taken from clinic's caller ID)

// EN-1255: prefill values (contacts.primary_phone and the live lookup's prefill) are the PIMS's raw
// string, e.g. "602-555-7098 mr" or ImproMed's "453-2577 (734)". Dial the matching entry's clean
// number when there is one; otherwise fall back to the raw string's digits as before.
// An ambiguous entry (its raw text held several distinct numbers, so the extracted one is a guess)
// is never prefilled: auto-dialing a guessed number could ring the wrong person. Return an empty
// field and let staff pick a row from the list or type the number.
const resolvePrefill = (phones: ContactPhone[], prefill: string | null | undefined): string => {
    const match = phones.find((phone) => phone.raw === prefill);
    if (match?.ambiguous) return "";
    return match?.number ?? prefill ?? "";
};

// Show the PIMS's raw string only when it says something the formatted number doesn't: initials,
// "ext", or digits in a different order (a trailing area code).
const rawAddsInfo = (phone: ContactPhone): boolean =>
    /[^-\d\s().+]/.test(phone.raw) || phone.raw.replace(/\D/g, "") !== phone.number;

const formatPhoneRow = (phone: ContactPhone): string =>
    phone.label ? `${phone.label} · ${formatPhoneForDisplay(phone.number)}` : formatPhoneForDisplay(phone.number);

/**
 * Dialer component for making phone calls
 * Features dial pad, caller ID selection, destination selection, and call states
 */
const Dialer = ({ onCancel, onAddCallerId, onAddDestination, onClientNumbersVisibleChange }: DialerProps) => {
    const [dialerState, setDialerState] = useState<DialerState>("idle");
    const [formattedDialedNumber, setFormattedDialedNumber] = useState<string>("");
    const [selectedCallerId, setSelectedCallerId] = useState<PhoneNumber | null>(null);
    const [selectedDestination, setSelectedDestination] = useState<PhoneNumber | null>(null);
    const [callDuration, setCallDuration] = useState<number>(0);
    const [currentCallId, setCurrentCallId] = useState<string | null>(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    // Snapshot of targetPatientRecordId taken the moment a call is confirmed. callTarget is
    // live context (can be reassigned by another RecordingModeButton click while this widget
    // is open), so once a call is in flight we must stop re-deriving the target from it —
    // otherwise handleEndCall would record the end against the wrong patient.
    const [lockedPatientRecordId, setLockedPatientRecordId] = useState<string | null>(null);
    const currentPatientRecordId = useAppSelector(getPatientRecordId);
    const currentPatientContact = useAppSelector(getPatientContact);
    const numberInputRef = useRef<HTMLInputElement>(null);
    const queryClient = useQueryClient();
    const { data: callStatus } = useCallStatus(currentCallId);

    // formattedDialedNumber is the source of truth and dialedNumber is the derived value
    const dialedNumber = useMemo(() => {
        return formattedDialedNumber.replace(/[^0-9]/g, "");
    }, [formattedDialedNumber]);

    const {
        phoneCallWidgetControls: { setRenderPhoneCallWidget, callTarget, setIsCallConnected },
    } = useAssetLibraryProvider();

    // Snapshot the effective call target once, at mount. PhoneCallWidget renders at the
    // app layout level and stays mounted across patient navigation for as long as the
    // widget is open, so deriving this live from callTarget/Redux on every render would
    // let the prefilled number silently swap to whichever patient page is currently
    // active while this dialer sits open and idle, before any call is confirmed.
    // PhoneCallWidget unmounts Dialer on close and mounts a fresh instance on next open,
    // so capturing this once at mount is equivalent to capturing it once per widget-open
    // — the entire lifetime that needs protecting. Once a call target is set (quick
    // start), its contact is authoritative — even if null (patient has no phone on
    // file) — never substitute a different patient's active Redux contact. Only fall
    // back to Redux when there is no call target at all (opened via the regular
    // ContactDetails path).
    const [{ patientRecordId: targetPatientRecordId, contact: targetContact }] = useState(() => ({
        patientRecordId: callTarget?.patientRecordId ?? currentPatientRecordId,
        contact: callTarget ? callTarget.contact : currentPatientContact,
    }));
    // EN-1255: every number the PIMS holds for this client: stored list now, live lookup shortly after.
    // Keyed off the mount snapshot, so it never follows another patient's page.
    // Trust boundary: this list is the server's answer for this one contact. The API endpoint
    // (API-Server POST /phone/contacts/:contactId/phones/refresh) authorizes the contact against the caller's
    // clinic account (404 otherwise), and the React Query cache is keyed by contactId with no placeholder or
    // previous data, so another contact's numbers can't be shown here. A typed number is the staff member's
    // explicit choice and is length-validated by handleCall as before.
    const { phones: contactPhones, prefill: refreshedPrefill, refreshed } = useContactPhones(targetContact);
    // Set once staff type, delete, pick a row, or press Call. After that a late lookup never touches the field.
    const fieldTouchedRef = useRef(false);
    const selectedContactPhone = contactPhones.find((phone) => phone.number === dialedNumber) ?? null;
    const showClientNumbers = dialerState === "idle" && contactPhones.length > 1;
    useEffect(() => {
        onClientNumbersVisibleChange?.(showClientNumbers);
    }, [showClientNumbers, onClientNumbersVisibleChange]);
    const {
        initiateCallMutation,
        endCallMutation,
        verifiedCallerIds,
        staffDestinationNumbers: destinations,
    } = usePhoneCall();

    // Focus the number input when the dialer is opened
    useEffect(() => {
        if (numberInputRef.current) {
            numberInputRef.current.focus();
        }
    }, []);

    // TODO: Save selected caller ID and destination to local storage and select new ones only when added
    // Auto-select first caller ID and destination if available
    useEffect(() => {
        if (verifiedCallerIds.length > 0) {
            // Caller IDs are sorted so the most recently added is selected
            setSelectedCallerId(verifiedCallerIds[0]);
        }
    }, [verifiedCallerIds]);

    useEffect(() => {
        if (destinations.length > 0) {
            // Destinations are sorted so the most recently added is selected
            setSelectedDestination(destinations[0]);
        }
    }, [destinations]);

    // Pre-fill dialed number from the explicit call target when present, else fall back
    // to the active-patient Redux contact (e.g. opened from ContactDetails). targetContact
    // is captured once at mount and never reassigned, so this only needs to run once —
    // depending on dialerState too would re-fire on every idle transition (e.g. after a
    // failed call), overwriting a number the user has since edited.
    useEffect(() => {
        if (targetContact) {
            setFormattedDialedNumber(
                formatDialedNumber(resolvePrefill(targetContact.phones ?? [], targetContact.primary_phone)),
            );
        }
    }, [targetContact]);

    // EN-1255: the live PIMS lookup lands after mount. Take its prefill only while the field still holds
    // the mount prefill, so a late response never replaces a number staff typed or picked.
    useEffect(() => {
        if (!refreshed || !refreshedPrefill || fieldTouchedRef.current) return;
        setFormattedDialedNumber(formatDialedNumber(resolvePrefill(contactPhones, refreshedPrefill)));
    }, [refreshed, refreshedPrefill, contactPhones]);

    // Report call-connection state to the header toggle (PatientBasicInfo) so it can
    // avoid closing the widget mid-call — closing here is only safe via handleEndCall,
    // which the footer already enforces by hiding Cancel once dialerState leaves "idle".
    useEffect(() => {
        setIsCallConnected(dialerState !== "idle");
        return () => setIsCallConnected(false);
    }, [dialerState, setIsCallConnected]);

    // Sync dialer state from realtime call-status-updated events
    useEffect(() => {
        if (!callStatus) return;
        if (callStatus.callStatus === "initiated" || callStatus.callStatus === "ringing") {
            setDialerState("calling");
        } else if (callStatus.callStatus === "in-progress") {
            setDialerState("in-call");
        } else if (callStatus.callStatus === "completed" || callStatus.callStatus === "ended") {
            // callTarget is cleared centrally by AssetLibraryProvider whenever renderPhoneCallWidget
            // goes false — this effect only needs to trigger the close.
            setRenderPhoneCallWidget(false);
            setDialerState("idle");
            setTimeout(() => {
                if (currentCallId) queryClient.removeQueries({ queryKey: [...CALL_STATUS_QUERY_KEY, currentCallId] });
                setCurrentCallId(null);
                setFormattedDialedNumber("");
            }, 1000);
        } else if (callStatus.callStatus === "failed") {
            setDialerState("idle");
            if (currentCallId) queryClient.removeQueries({ queryKey: [...CALL_STATUS_QUERY_KEY, currentCallId] });
            setCurrentCallId(null);
        }
    }, [callStatus?.callStatus, currentCallId, queryClient]);

    // Call duration timer
    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;

        if (dialerState === "in-call") {
            interval = setInterval(() => {
                setCallDuration((prev) => prev + 1);
            }, 1000);
        } else {
            setCallDuration(0);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [dialerState]);

    const formatDuration = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hours > 0) {
            return `${hours}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
        }
        return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };

    const handleDigitPress = useCallback(
        (digit: string) => {
            if (dialerState !== "idle" || dialedNumber.length >= MAX_DIALED_DIGITS) return;
            fieldTouchedRef.current = true;
            setFormattedDialedNumber((prev) => {
                const start = numberInputRef.current?.selectionStart ?? numberInputRef.current?.value.length ?? 0;
                const end = numberInputRef.current?.selectionEnd ?? numberInputRef.current?.value.length ?? 0;
                const nextValue = prev.slice(0, start) + digit + prev.slice(end);
                const nextValueFormatted = formatDialedNumber(nextValue);
                const formattedCharsAdded = nextValueFormatted.length - prev.length;
                setTimeout(() => {
                    numberInputRef.current?.focus();
                    numberInputRef.current?.setSelectionRange(start + formattedCharsAdded, start + formattedCharsAdded);
                }, 0);
                return nextValueFormatted;
            });
        },
        [dialerState, dialedNumber.length, numberInputRef],
    );

    const handleBackspace = useCallback(() => {
        if (dialerState !== "idle") return;
        fieldTouchedRef.current = true;
        setFormattedDialedNumber((prev) => {
            const start = numberInputRef.current?.selectionStart ?? numberInputRef.current?.value.length ?? 0;
            if (start === 0) {
                return prev;
            }
            const end = numberInputRef.current?.selectionEnd ?? numberInputRef.current?.value.length ?? 0;
            const nextValue = prev.slice(0, start - 1) + prev.slice(end);
            const nextValueFormatted = formatDialedNumber(nextValue);
            setTimeout(() => {
                numberInputRef.current?.focus();
                numberInputRef.current?.setSelectionRange(start - 1, start - 1);
            }, 0);
            return nextValueFormatted;
        });
        numberInputRef.current?.focus();
    }, [dialerState, numberInputRef]);

    const handleInputChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            if (dialerState !== "idle") return;
            fieldTouchedRef.current = true;
            // Extract only valid phone characters (digits, *, #)
            const rawValue = e.target.value.replace(/[^0-9\*#]/g, "");
            // Limit to MAX_DIALED_DIGITS digits
            if (rawValue.length > MAX_DIALED_DIGITS) return;
            setFormattedDialedNumber(formatDialedNumber(rawValue));
        },
        [dialerState],
    );

    const handlePickContactPhone = (phone: ContactPhone) => {
        if (dialerState !== "idle") return;
        // Enforced here, not just via the row's disabled attribute: a "needs area code" number can't be dialed.
        if (phone.number.length < MIN_DIALED_DIGITS_FOR_CALL) return;
        fieldTouchedRef.current = true;
        setFormattedDialedNumber(formatDialedNumber(phone.number));
        numberInputRef.current?.focus();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && dialerState === "idle" && dialedNumber.length >= MIN_DIALED_DIGITS_FOR_CALL) {
            e.preventDefault();
            handleCall();
        }
    };

    const callerIdCountryCode = useMemo(
        () => selectedCallerId?.phoneNumber.slice(0, -MIN_DIALED_DIGITS_FOR_CALL) ?? "",
        [selectedCallerId],
    );

    const handleCall = () => {
        // Pressing Call commits to the shown number: a lookup landing under the confirm modal must not swap it.
        fieldTouchedRef.current = true;
        if (!selectedCallerId || !selectedDestination || dialedNumber.length < MIN_DIALED_DIGITS_FOR_CALL) {
            toast.error("Please select a caller ID and destination and enter a valid phone number");
            return;
        }
        setShowConfirmModal(true);
    };

    const handleConfirmCall = async () => {
        if (!selectedCallerId || !selectedDestination) return;

        setShowConfirmModal(false);
        setDialerState("calling");
        setLockedPatientRecordId(targetPatientRecordId ?? null);

        try {
            const clientPhoneNumber =
                dialedNumber.length > MIN_DIALED_DIGITS_FOR_CALL
                    ? `+${dialedNumber}`
                    : `${callerIdCountryCode}${dialedNumber}`;
            const result = await initiateCallMutation.mutateAsync({
                patientRecordId: targetPatientRecordId ?? "",
                client_phone_number: clientPhoneNumber,
                staff_phone_number: selectedDestination.phoneNumber,
                account_caller_id: selectedCallerId.id,
                ...(selectedDestination.extension && { staff_extension: selectedDestination.extension }),
            });

            setCurrentCallId(result.callSid);
            voipAnalytics.outboundCallInitiated(
                targetPatientRecordId ? { patient_record_id: targetPatientRecordId } : {},
            );
        } catch (error) {
            console.error("Failed to initiate call:", error);
            setDialerState("idle");
        }
    };

    const handleEndCall = async () => {
        if (currentCallId && lockedPatientRecordId) {
            try {
                await endCallMutation.mutateAsync({
                    patientRecordId: lockedPatientRecordId,
                    callSid: currentCallId,
                });
            } catch (error) {
                console.error("Failed to end call:", error);
            }
        }
        // callTarget is cleared centrally by AssetLibraryProvider whenever renderPhoneCallWidget
        // goes false — this handler only needs to trigger the close.
        setRenderPhoneCallWidget(false);
        setDialerState("idle");
        setLockedPatientRecordId(null);
        setTimeout(() => {
            if (currentCallId) queryClient.removeQueries({ queryKey: [...CALL_STATUS_QUERY_KEY, currentCallId] });
            setCurrentCallId(null);
            setFormattedDialedNumber("");
        }, 1000);
    };

    const canMakeCall = selectedCallerId && selectedDestination && dialedNumber.length >= MIN_DIALED_DIGITS_FOR_CALL;
    const showDialerCountryCode =
        callerIdCountryCode &&
        !callerIdCountryCode.startsWith("+1") &&
        dialedNumber.length <= MIN_DIALED_DIGITS_FOR_CALL;

    return (
        <div
            className={cn("absolute bottom-0 w-full flex flex-col bg-shadow-950 rounded-lg shadow overflow-y-auto", {
                "h-full": dialerState === "idle", // Use outer widget height for animation
                "h-auto": dialerState !== "idle", // Use inner content height for non-idle states
            })}
        >
            {/* Header - shadow-850 #232C33, 210px */}
            <div className="shrink-0 px-6 py-5 flex flex-col rounded-t-lg gap-2 bg-shadow-850">
                <h3 className="text-white text-lg font-semibold">Phone Call</h3>

                {dialerState === "idle" ? (
                    // Dropdowns for caller ID and destination
                    <div className="flex flex-col gap-3">
                        {/* Caller ID Dropdown */}
                        <Popover className="relative">
                            <PopoverButton className="w-full bg-shadow-600 rounded-md px-3 py-2 flex justify-between items-center text-left outline-none focus:ring-2 ring-inset ring-max-500">
                                <div className="flex flex-col gap-0.5">
                                    <div className="text-white text-sm font-medium">Caller ID</div>
                                    <div className="text-white text-sm font-medium tracking-wider">
                                        {selectedCallerId
                                            ? formatPhoneForDisplay(selectedCallerId.phoneNumber)
                                            : "Select Caller ID"}
                                    </div>
                                </div>
                                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                            </PopoverButton>
                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <PopoverPanel className="absolute top-full left-0 right-0 mt-1 bg-shadow-500 rounded-md shadow-lg z-10 max-h-40 overflow-y-auto">
                                    {verifiedCallerIds.map((callerId) => (
                                        <CloseButton
                                            as="button"
                                            key={callerId.id}
                                            onClick={() => setSelectedCallerId(callerId)}
                                            className="w-full px-3 py-2 text-left text-white text-sm font-medium hover:bg-shadow-600 rounded-md outline-none focus:ring-2 ring-inset ring-max-500"
                                        >
                                            {callerId.referenceName} - {formatPhoneForDisplay(callerId.phoneNumber)}
                                        </CloseButton>
                                    ))}
                                    <CloseButton
                                        as="button"
                                        onClick={onAddCallerId}
                                        className="w-full px-3 py-2 text-left text-max-500 text-sm hover:bg-shadow-600 border-t border-shadow-800 rounded-md outline-none focus:ring-2 ring-inset ring-max-500"
                                    >
                                        + Add New Caller ID
                                    </CloseButton>
                                </PopoverPanel>
                            </Transition>
                        </Popover>

                        {/* Destination Dropdown */}
                        <Popover className="relative">
                            <PopoverButton className="w-full bg-shadow-600 rounded-md px-3 py-2 flex justify-between items-center text-left outline-none focus:ring-2 ring-inset ring-max-500">
                                <div className="flex flex-col gap-0.5">
                                    <div className="text-white text-sm font-medium">My Phone Number</div>
                                    <div className="text-white text-sm font-medium tracking-wider">
                                        {selectedDestination
                                            ? formatDestination(
                                                  formatPhoneForDisplay(selectedDestination.phoneNumber),
                                                  selectedDestination.extension,
                                              )
                                            : "Select Destination"}
                                    </div>
                                </div>
                                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                            </PopoverButton>
                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <PopoverPanel className="absolute top-full left-0 right-0 mt-1 bg-shadow-500 rounded-md shadow-lg z-10 max-h-40 overflow-y-auto">
                                    {destinations.map((dest) => (
                                        <CloseButton
                                            as="button"
                                            key={dest.id}
                                            onClick={() => setSelectedDestination(dest)}
                                            className="w-full px-3 py-2 text-left text-white text-sm font-medium hover:bg-shadow-600 rounded-md outline-none focus:ring-2 ring-inset ring-max-500"
                                        >
                                            {dest.referenceName} -{" "}
                                            {formatDestination(formatPhoneForDisplay(dest.phoneNumber), dest.extension)}
                                        </CloseButton>
                                    ))}
                                    <CloseButton
                                        as="button"
                                        onClick={onAddDestination}
                                        className="w-full px-3 py-2 text-left text-max-500 text-sm hover:bg-shadow-600 border-t border-shadow-800 rounded-md outline-none focus:ring-2 ring-inset ring-max-500"
                                    >
                                        + Add New Destination
                                    </CloseButton>
                                </PopoverPanel>
                            </Transition>
                        </Popover>
                    </div>
                ) : (
                    // Show caller ID info during call
                    <div className="text-gray-400 text-sm">
                        <div>Caller ID</div>
                        <div className="text-white">
                            {selectedCallerId ? formatPhoneForDisplay(selectedCallerId.phoneNumber) : ""}
                        </div>
                    </div>
                )}
            </div>

            {/* Dial pad area */}
            <div className="shrink-0 bg-shadow-950 px-6 py-5 flex flex-col">
                {/* Status indicator */}
                {dialerState !== "idle" && (
                    <div className="flex justify-center mb-3 -mt-2">
                        <span
                            className={`px-3 py-0.5 rounded-md text-sm font-medium ${
                                dialerState === "calling" ? "text-white" : "bg-green-50 text-green-700"
                            }`}
                        >
                            {dialerState === "calling" ? (
                                callStatus?.callStatus === "ringing" ? (
                                    "Ringing..."
                                ) : (
                                    "Calling your phone..."
                                )
                            ) : (
                                <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                                    Call in Progress
                                </span>
                            )}
                        </span>
                    </div>
                )}

                {/* Dialed number input */}
                <div className="relative bg-shadow-700 rounded-lg mb-5 flex items-center">
                    <input
                        ref={numberInputRef}
                        type="tel"
                        value={formattedDialedNumber}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        disabled={dialerState !== "idle"}
                        placeholder={dialerState === "idle" ? "(    )    –    " : ""}
                        className={cn(
                            "w-full flex-1 -mt-0.5 !overflow-visible bg-transparent rounded-lg border-none text-white text-2xl font-semibold tracking-wide outline-none placeholder:relative placeholder:-left-3 placeholder:text-gray-500 placeholder:text-2xl placeholder:font-normal focused-inset disabled:cursor-not-allowed",
                            {
                                "px-20": dialedNumber.length === 0,
                                "pl-16 pr-14":
                                    dialedNumber.length > 0 && dialedNumber.length <= MIN_DIALED_DIGITS_FOR_CALL,
                                "pl-2 pr-[60px] text-right":
                                    dialedNumber.length > MIN_DIALED_DIGITS_FOR_CALL &&
                                    dialedNumber.length < MAX_DIALED_DIGITS,
                                "pl-1 pr-12 text-right": dialedNumber.length === MAX_DIALED_DIGITS,
                            },
                        )}
                    />
                    {showDialerCountryCode && (
                        <div className="absolute left-3 text-gray-400 text-2xl font-semibold">
                            {callerIdCountryCode}
                        </div>
                    )}
                    {dialedNumber.length > 0 && dialerState === "idle" && (
                        <button
                            tabIndex={-1}
                            onClick={handleBackspace}
                            className="absolute right-3 text-gray-400 hover:text-white transition-colors outline-none focus:ring-2 ring-inset ring-max-500"
                            aria-label="Delete last digit"
                        >
                            <BackspaceIcon className="w-5 h-5" />
                        </button>
                    )}
                </div>

                {/* EN-1255: every number the PIMS holds for this client. Picking one fills the field, which stays editable. */}
                {showClientNumbers && (
                    <Popover className="relative -mt-2 mb-4">
                        <PopoverButton className="w-full bg-shadow-600 rounded-md px-3 py-2 flex justify-between items-center text-left outline-none focus:ring-2 ring-inset ring-max-500">
                            <div className="flex flex-col gap-0.5">
                                <div className="text-white text-sm font-medium">Client numbers</div>
                                <div className="text-white text-sm font-medium tracking-wider">
                                    {selectedContactPhone ? formatPhoneRow(selectedContactPhone) : "Select a number"}
                                </div>
                            </div>
                            <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                        </PopoverButton>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                        >
                            <PopoverPanel className="absolute top-full left-0 right-0 mt-1 bg-shadow-500 rounded-md shadow-lg z-10 max-h-40 overflow-y-auto">
                                {contactPhones.map((phone, index) => {
                                    // Short local numbers (7 digits, no area code) can't be dialed as-is: Call rejects
                                    // anything under MIN_DIALED_DIGITS_FOR_CALL, so the row is shown but not pickable.
                                    const needsAreaCode = phone.number.length < MIN_DIALED_DIGITS_FOR_CALL;
                                    const isSelected = !needsAreaCode && phone.number === selectedContactPhone?.number;
                                    return (
                                        <CloseButton
                                            as="button"
                                            key={`${phone.number}-${index}`}
                                            aria-pressed={isSelected}
                                            aria-disabled={needsAreaCode}
                                            disabled={needsAreaCode}
                                            onClick={() => handlePickContactPhone(phone)}
                                            className={cn(
                                                "w-full px-3 py-2 text-left text-white text-sm font-medium hover:bg-shadow-600 rounded-md outline-none focus:ring-2 ring-inset ring-max-500",
                                                {
                                                    "bg-shadow-600": isSelected,
                                                    "opacity-50 cursor-not-allowed hover:bg-transparent": needsAreaCode,
                                                },
                                            )}
                                        >
                                            {formatPhoneRow(phone)}
                                            {needsAreaCode && (
                                                <span className="ml-2 text-gray-400 font-normal">needs area code</span>
                                            )}
                                            {phone.ambiguous && (
                                                <span className="ml-2 text-gray-400 font-normal">verify number</span>
                                            )}
                                            {(phone.ambiguous || rawAddsInfo(phone)) && (
                                                <span className="ml-2 text-gray-400 font-normal">{phone.raw}</span>
                                            )}
                                        </CloseButton>
                                    );
                                })}
                            </PopoverPanel>
                        </Transition>
                    </Popover>
                )}

                {/* Dial pad */}
                <div className="w-[220px] mx-auto grid grid-cols-3 gap-4 justify-center">
                    {DIAL_PAD_BUTTONS.map(({ digit, letters }) => (
                        <button
                            key={digit}
                            tabIndex={-1}
                            onClick={() => handleDigitPress(digit)}
                            disabled={dialerState !== "idle"}
                            className="bg-shadow-600 rounded-full aspect-square flex flex-col items-center justify-center hover:bg-shadow-500 active:bg-shadow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed outline-none focus:ring-2 ring-inset ring-max-500"
                        >
                            <span
                                className={cn(
                                    "text-[1.375rem] leading-tight",
                                    digit === "*" || digit === "#"
                                        ? "text-shadow-400 font-light"
                                        : "text-white font-medium",
                                )}
                            >
                                {digit}
                            </span>
                            {letters && (
                                <span className="text-shadow-400 text-xs tracking-wider leading-tight">{letters}</span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Footer buttons */}
            <div className="px-6 py-5 bg-shadow-800 flex gap-3 rounded-b-lg items-center">
                {dialerState === "idle" ? (
                    <>
                        <button
                            onClick={onCancel}
                            className="secondary-button button-sm h-10 flex-1 !text-sm !text-max-800"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCall}
                            disabled={!canMakeCall || initiateCallMutation.isPending}
                            className="primary-button button-sm h-10 flex-1 !text-sm"
                        >
                            <PhoneIcon className="w-5 h-5" />
                            Call
                        </button>
                    </>
                ) : (
                    <>
                        {dialerState === "in-call" && (
                            <span
                                className={cn("block text-white text-xl font-semibold", {
                                    "w-20 ml-2": callDuration < 3600, // less than 1 hour
                                    "w-20 mr-2": callDuration >= 3600, // more than 1 hour
                                })}
                            >
                                {formatDuration(callDuration)}
                            </span>
                        )}
                        <button onClick={handleEndCall} className="red-button button-sm h-10 flex-1 !text-sm">
                            End Call
                        </button>
                    </>
                )}
            </div>

            {/* Confirmation Modal */}
            <ConfirmModal
                id="placing-call-modal"
                headerText="Placing Call"
                isOpen={showConfirmModal}
                onCancel={() => setShowConfirmModal(false)}
                onConfirm={handleConfirmCall}
                cancelButtonText="Cancel"
                confirmButtonText="Confirm"
                confirmButtonColor="bg-max-600"
                confirmHoverButtonColor="bg-max-900"
                content={
                    <p className="text-sm text-gray-500">
                        We'll connect your number{" "}
                        <span className="font-semibold text-gray-700">
                            {selectedDestination
                                ? formatDestination(
                                      formatPhoneForDisplay(selectedDestination.phoneNumber),
                                      selectedDestination.extension,
                                  )
                                : ""}
                        </span>{" "}
                        with the patient's{" "}
                        <span className="font-semibold text-gray-700">
                            {formatPhoneForDisplay(
                                dialedNumber.length > MIN_DIALED_DIGITS_FOR_CALL
                                    ? dialedNumber
                                    : `${callerIdCountryCode}${dialedNumber}`,
                            )}
                        </span>
                        . Once finished, you can access the recording from the patient's library.
                    </p>
                }
            />
        </div>
    );
};

export default Dialer;
